import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdEdit, MdDelete, MdVisibility, MdClose, MdSearch } from 'react-icons/md';
import ImageCropper from '../components/ImageCropper';
import WebPreviewModal from '../components/WebPreviewModal';
import Pagination from '../components/Pagination';
import { useAuth } from '../context/AuthContext';
import { formatDateTime, formatINR } from '../utils/format';
import { toEnglishAuthorName } from '../utils/authorUtils';

function SearchableSelect({ label, value, options, onChange, placeholder = "Search...", searchKey = "slug" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = options.filter(opt =>
    (opt.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (opt[searchKey] || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="form-group">
      <label>{label}</label>
      <div className="searchable-select">
        <div className="select-box" onClick={() => setIsOpen(!isOpen)}>
          {(typeof value === 'string' && (value.trim() === 'అల్ ఇండియా' || value.trim() === 'అల్ ఇండియా ') ? 'All India' : value) || `Select ${label}`}
        </div>
        {isOpen && (
          <div className="select-dropdown">
            <input
              autoFocus
              placeholder={placeholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
            <div className="options-list">
              {filtered.map(opt => (
                <div
                  key={opt.id || opt.name}
                  className="option-item"
                  onClick={() => {
                    onChange(opt.name);
                    setIsOpen(false);
                    setSearch('');
                  }}
                >
                  <span>{(opt.name?.trim() === 'అల్ ఇండియా' || opt.name?.trim() === 'అల్ ఇండియా ') ? 'All India' : opt.name}</span>
                  {opt[searchKey] && <span className="text-muted text-xs">({opt[searchKey]})</span>}
                </div>
              ))}
              {filtered.length === 0 && <div className="option-item text-muted">{options.length === 0 ? `No ${label.toLowerCase()}s available` : "No results found"}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Articles() {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isTopEarning = queryParams.get('top_earning') === 'true';
  const topContributorId = queryParams.get('contributor_id');

  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [reporters, setReporters] = useState([]);
  const [articles, setArticles] = useState([]);
  const [tab, setTab] = useState('all');
  const [filters, setFilters] = useState({ category: '', city: '', status: admin?.role === 'reporter' ? 'draft' : '', admin_id: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);

  const stateMapping = {
    'ఆంధ్రప్రదేశ్': 'Andhra Pradesh',
    'తెలంగాణ': 'Telangana',
    'అల్ ఇండియా': 'All India',
    'అల్ ఇండియా ': 'All India',
    'ఆంధ్ర ప్రదేశ్': 'Andhra Pradesh',
    'Telangana': 'Telangana',
    'Andhra Pradesh': 'Andhra Pradesh'
  };

  // Edit modal
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});

  // View modal
  const [viewModal, setViewModal] = useState(null);

  // Delete confirmation modal
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchArticles = useCallback(async (page = 1) => {
    setLoading(true);
    window.scrollTo(0, 0);
    try {
      if (isTopEarning && topContributorId) {
        const res = await api.get(`/admin/contributors/${topContributorId}/top-earning-articles`);
        setArticles(res.data.data);
        setPagination({ page: 1, pages: 1, total: res.data.data.length });
        setLoading(false);
        return;
      }

      const params = { page, limit: 15 };
      if (tab === 'archive') {
        const res = await api.get('/admin/articles/archive', { params });
        setArticles(res.data.data);
        setPagination(res.data.pagination);
      } else {
        if (filters.category) params.category = filters.category;
        if (filters.city) params.city = filters.city;
        if (filters.admin_id) params.admin_id = filters.admin_id;
        params.status = filters.status || 'published';
        if (searchQuery.trim()) params.search = searchQuery.trim();
        const res = await api.get('/admin/articles', { params });
        setArticles(res.data.data);
        setPagination(res.data.pagination);
      }
    } catch (err) {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, [tab, filters, searchQuery, isTopEarning, topContributorId]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchArticles(1);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, tab, filters, fetchArticles]);

  useEffect(() => {
    const searchFromUrl = queryParams.get('search');
    if (searchFromUrl && searchFromUrl !== searchQuery) {
      setSearchQuery(searchFromUrl);
    }
  }, [location.search]);

  useEffect(() => {
    const idFromUrl = queryParams.get('id');
    if (idFromUrl && articles.length > 0) {
      const match = articles.find(a => String(a.id) === String(idFromUrl));
      if (match) {
        setViewModal(match);
      }
    }
  }, [location.search, articles]);

  useEffect(() => {
    api.get('/categories').then(res => setCategories(res.data.data)).catch(() => { });
    api.get('/cities').then(res => setCities(res.data.data)).catch(() => { });
    if (admin?.role === 'admin') {
      api.get('/admin/admins').then(res => {
        setReporters(res.data.data);
      }).catch(() => { });
    }
  }, [admin]);

  const filteredArticles = articles;

  const handleAiGenerate = async (type, extraData = null) => {
    if (type === 'slug' && !editForm.title) {
      return toast.error('Please enter a title first');
    }
    if (type === 'transliterate' && !extraData) {
      return toast.error('Please enter some text to convert');
    }

    setLoading(true);
    try {
      const payload = { type };
      if (type === 'slug') payload.title = editForm.title;
      if (type === 'transliterate') payload.text = extraData;

      const res = await api.post('/admin/ai/generate', payload);
      if (res.data.success) {
        if (type === 'slug') setEditForm(f => ({ ...f, slug: res.data.data }));
        if (type === 'author') setEditForm(f => ({ ...f, author_name: res.data.data }));
        if (type === 'transliterate') {
          return res.data.data;
        }
        toast.success('Generated');
      }
    } catch (err) {
      toast.error('AI operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleTransliterateEdit = async (field) => {
    const text = editForm[field];
    if (!text) return toast.error(`Please type something in ${field} first`);
    const result = await handleAiGenerate('transliterate', text);
    if (result) {
      const limit = field === 'title' ? 75 : field === 'summary' ? 400 : result.length;
      setEditForm(f => ({ ...f, [field]: result.slice(0, limit) }));
    }
  };

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/articles/${deleteModal.id}`);
      toast.success('Article deleted');
      setDeleteModal(null);
      fetchArticles(pagination.page);
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const [editUploading, setEditUploading] = useState(false);
  const [editGalleryUploading, setEditGalleryUploading] = useState(false);
  const [showEditWebPreview, setShowEditWebPreview] = useState(false);
  const [editCropper, setEditCropper] = useState({ active: false, image: null, type: null });

  const [editState, setEditState] = useState('');
  const [editCities, setEditCities] = useState([]);

  const handleEditGalleryFilesUpload = async (files) => {
    if (!files || files.length === 0) return;
    setEditGalleryUploading(true);
    const newUrls = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/admin/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.data?.data?.url) {
          newUrls.push(res.data.data.url);
        }
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (newUrls.length > 0) {
      setEditForm(prev => ({
        ...prev,
        images: [...(prev.images || []), ...newUrls]
      }));
      toast.success(`${newUrls.length} gallery image(s) uploaded`);
    }
    setEditGalleryUploading(false);
  };

  const moveEditGalleryImage = (index, direction) => {
    setEditForm(prev => {
      const arr = [...(prev.images || [])];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= arr.length) return prev;
      const temp = arr[index];
      arr[index] = arr[targetIndex];
      arr[targetIndex] = temp;
      return { ...prev, images: arr };
    });
  };

  const removeEditGalleryImage = (index) => {
    setEditForm(prev => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index)
    }));
  };

  const handleEditGalleryPasteImage = (e) => {
    const items = e.clipboardData?.items || [];
    const filesToUpload = [];
    for (const it of items) {
      if (it.type && it.type.startsWith('image/')) {
        const file = it.getAsFile();
        if (file) {
          filesToUpload.push(file);
        }
      }
    }
    if (filesToUpload.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      handleEditGalleryFilesUpload(filesToUpload);
    }
  };

  const openEdit = (article) => {
    setEditModal(article);
    const cityObj = cities.find(c => c.name === article.city);
    const initialState = cityObj ? cityObj.state : '';
    setEditState(initialState);
    setEditCities(cities.filter(c => c.state === initialState));

    setEditForm({
      title: article.title || '',
      summary: article.summary || '',
      category: article.category || '',
      city: article.city || '',
      source_url: article.source_url || '',
      source: article.source || '',
      media_type: article.media_type || 'image',
      image_url: article.image_url || '',
      thumbnail_url: article.thumbnail_url || '',
      video_url: article.video_url || '',
      is_featured: article.is_featured || false,
      slug: (article.slug || '').split('-').slice(0, -1).join('-'), // Show without ID
      author_name: article.author_name || '',
      images: article.images ? article.images.map(img => img.image_url) : [],
      status: article.status || 'draft',
      manual_thumbnail: false,
    });
  };

  const handleEditFileSelect = (fileOrType, type) => {
    if (typeof fileOrType === 'string') {
      const currentType = fileOrType;
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = currentType === 'video' ? 'video/mp4' : 'image/*';

      // Store reference to prevent garbage collection
      window._tempFileInput = input;

      input.onchange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const selectedFile = files[0];
        if (currentType === 'video') {
          uploadEditToServer(selectedFile, currentType);
        } else {
          const reader = new FileReader();
          reader.onload = () => {
            setEditCropper({ active: true, image: reader.result, type: currentType });
          };
          reader.readAsDataURL(selectedFile);
        }
      };
      input.click();
      return;
    }

    const file = fileOrType;
    if (type === 'video') {
      uploadEditToServer(file, type);
    } else {
      if (file && file instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          setEditCropper({ active: true, image: reader.result, type });
        };
        reader.readAsDataURL(file);
      }
    }
  };

  useEffect(() => {
    const handleGlobalPaste = (e) => {
      if (!editModal) return;
      if (editForm?.media_type !== 'image') return;
      if (editCropper.active || showEditWebPreview) return;

      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type && it.type.startsWith('image/')) {
          const file = it.getAsFile();
          if (file) {
            e.preventDefault();
            const reader = new FileReader();
            reader.onload = () => {
              setEditCropper({ active: true, image: reader.result, type: 'image' });
            };
            reader.readAsDataURL(file);
            return;
          }
        }
      }
    };

    window.addEventListener('paste', handleGlobalPaste);
    return () => window.removeEventListener('paste', handleGlobalPaste);
  }, [editModal, editForm?.media_type, editCropper.active, showEditWebPreview]);

  const handleEditPasteImage = (e) => {
    e.preventDefault();
  };

  const handleEditCropComplete = async (blob) => {
    const type = editCropper.type;
    setEditCropper({ active: false, image: null, type: null });

    const file = new File([blob], `cropped-${Date.now()}.jpg`, { type: 'image/jpeg' });
    uploadEditToServer(file, type);
  };

  const uploadEditToServer = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    const endpoint = (type === 'image' || type === 'extra_image') ? '/admin/upload/image'
      : type === 'thumbnail' ? '/admin/upload/thumbnail'
        : '/admin/upload/video';

    setEditUploading(true);
    try {
      const res = await api.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.data.url;
      const thumbnailUrl = res.data.data.thumbnail_url;

      if (type === 'image') setEditForm(f => ({ ...f, image_url: url }));
      else if (type === 'extra_image') setEditForm(f => ({ ...f, images: [...(f.images || []), url] }));
      else if (type === 'thumbnail') {
        setEditForm(f => ({ ...f, thumbnail_url: url, manual_thumbnail: true }));
      }
      else if (type === 'video') {
        setEditForm(f => {
          const nextForm = { ...f, video_url: url };
          if (thumbnailUrl && !f.manual_thumbnail) {
            nextForm.thumbnail_url = thumbnailUrl;
          }
          return nextForm;
        });
      }
      toast.success(`${type === 'extra_image' ? 'Image' : type} uploaded and resized!`);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setEditUploading(false);
    }
  };

  const saveEdit = async () => {
    if (!editModal) return;
    try {
      await api.put(`/admin/articles/${editModal.id}`, editForm);
      toast.success('Article updated');
      setEditModal(null);
      fetchArticles(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  const handleEditSubmit = (e) => {
    if (e) e.preventDefault();
    if (!editForm.title || !editForm.summary) return toast.error('Title and summary required');
    if (editForm.media_type === 'image' && (!editForm.image_url || !editForm.image_url.trim())) {
      return toast.error('Please upload the Article Image (Web & App).');
    }
    if (editForm.media_type === 'video') {
      if (!editForm.video_url || !editForm.video_url.trim()) {
        return toast.error('Please upload the Article Video.');
      }
      if (!editForm.thumbnail_url || !editForm.thumbnail_url.trim()) {
        return toast.error('Please upload the Article Video Thumbnail.');
      }
    }
    saveEdit();
  };

  const isLive = (article) => !article.is_archived && new Date(article.expires_at) > new Date() && article.status === 'published';

  return (
    <div className="page">

      {!isTopEarning && (
        <div className="page-header">
          <h2>Published Articles</h2>
          <p className="text-muted">Manage all published articles across categories and cities.</p>
        </div>
      )}

      {!isTopEarning && (
        <div className="tabs">
          <button className={`tab ${tab === 'all' ? 'active' : ''}`} onClick={() => setTab('all')}>All Articles</button>
          <button className={`tab ${tab === 'archive' ? 'active' : ''}`} onClick={() => setTab('archive')}>Archive</button>
        </div>
      )}

      {!isTopEarning && tab === 'all' && (
        <>
          <div className="filter-bar">
            <div className="search-input-wrap">
              <MdSearch className="search-icon" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles by title..."
                className="search-input"
              />
            </div>
            <select value={filters.category} onChange={(e) => setFilters(f => ({ ...f, category: e.target.value, city: '' }))}>
              <option value="">All Categories</option>
              {categories.map(c => <option key={c.slug} value={c.name}>{c.name}</option>)}
            </select>
            <select value={filters.city} onChange={(e) => setFilters(f => ({ ...f, city: e.target.value }))}>
              <option value="">All Cities</option>
              {(() => {
                const available = cities.filter(c => {
                  if (!filters.category) return true;
                  const cat = filters.category.trim();
                  const isStateCategory = ['తెలంగాణ', 'Telangana', 'ఆంధ్రప్రదేశ్', 'ఆంధ్ర ప్రదేశ్', 'Andhra Pradesh'].includes(cat);
                  if (!isStateCategory) return true;
                  return c.state === cat || c.state === stateMapping[cat];
                });
                if (available.length === 0 && filters.category) {
                  return <option value="" disabled>No cities available</option>;
                }
                return available.map(c => <option key={c.slug || c.id} value={c.name}>{c.name}</option>);
              })()}
            </select>
            {admin?.role === 'admin' && (
              <select value={filters.admin_id} onChange={(e) => setFilters(f => ({ ...f, admin_id: e.target.value }))}>
                <option value="">All Users</option>
                {reporters.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            )}
            {admin?.role === 'reporter' ? (
              <select value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}>
                <option value="draft">Draft Articles</option>
                <option value="">All Published</option>
                <option value="live">Active (Live) Articles</option>
                <option value="scheduled">Scheduled</option>
                <option value="expired">Expired Articles</option>
                <option value="past_12_hours">Past 12 Hours</option>
                <option value="past_24_hours">Past 24 Hours</option>
              </select>
            ) : (
              <select value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}>
                <option value="">All Published</option>
                <option value="live">Live (Active)</option>
                <option value="scheduled">Scheduled</option>
                <option value="expired">Expired</option>
                <option value="past_12_hours">Past 12 Hours</option>
                <option value="past_24_hours">Past 24 Hours</option>
              </select>
            )}
          </div>

          {(filters.category || (admin?.role === 'admin' && filters.admin_id)) && (
            <div style={{ display: 'flex', gap: '10px', marginTop: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              {admin?.role === 'admin' && filters.admin_id && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  background: '#ECF0FF',
                  border: '1.5px solid #C7D2FE',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#4F46E5',
                  fontFamily: 'Ramabhadra, serif',
                }}>
                  📝 {reporters.find(r => r.id == filters.admin_id)?.name}
                  <button onClick={() => setFilters(f => ({ ...f, admin_id: '' }))} style={{ background: 'none', border: 'none', color: '#4F46E5', cursor: 'pointer', fontSize: '16px', padding: '0', display: 'flex', alignItems: 'center', lineHeight: '1' }}>×</button>
                </div>
              )}
              {filters.category && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  background: '#FEF3C7',
                  border: '1.5px solid #FCD34D',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#B45309',
                  fontFamily: 'Ramabhadra, serif',
                }}>
                  📂 {filters.category}
                  <button onClick={() => setFilters(f => ({ ...f, category: '', city: '' }))} style={{ background: 'none', border: 'none', color: '#B45309', cursor: 'pointer', fontSize: '16px', padding: '0', display: 'flex', alignItems: 'center', lineHeight: '1' }}>×</button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {loading ? <div className="page-loader">Loading...</div> : (
        <>
          {isTopEarning ? (
            <div className="table-wrap">
              <table className="table" style={{ fontSize: 13 }}>
                <thead>
                  <tr><th>#</th><th>Article Title</th><th>Published Date</th><th>Reads</th><th>Earnings (₹)</th></tr>
                </thead>
                <tbody>
                  {articles.map((art, idx) => (
                    <tr key={art.id}>
                      <td>{idx + 1}</td>
                      <td>{art.title}</td>
                      <td>{formatDateTime(art.published_at)}</td>
                      <td>{art.read_count}</td>
                      <td style={{ fontWeight: 600 }}>{formatINR(art.earnings)}</td>
                    </tr>
                  ))}
                  {articles.length === 0 && <tr><td colSpan="5" className="empty-row">No top earning articles found</td></tr>}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th className="hide-mobile">Image</th>
                    <th>Title</th>
                    <th>Author</th>
                    <th className="hide-mobile">Views</th>
                    <th className="hide-mobile">Likes</th>
                    <th className="hide-mobile">Date</th>
                    <th className="hide-mobile">Time</th>
                    <th>Status</th>
                    {filters.status === 'scheduled' && <th>Scheduled Publish</th>}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredArticles.map(a => {
                    const publishedDate = new Date(a.published_at);
                    const dateStr = publishedDate.toLocaleDateString('en-GB');
                    const timeStr = publishedDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                    const imageSrc = a.thumbnail_url || a.image_url || '/placeholder.png';

                    return (
                      <tr key={a.id}>
                        <td className="hide-mobile" style={{ textAlign: 'center', padding: '6px' }}>
                          <img src={imageSrc} alt={a.title} style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px' }} onError={(e) => e.target.style.display = 'none'} />
                        </td>
                        <td className="title-cell">
                          <span className="article-title">{a.title}</span>
                          <div style={{ fontSize: '11px', marginTop: '4px', display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ color: '#EA580C', padding: '2px 8px', background: '#FFEDD5', borderRadius: '4px', fontWeight: 600 }}>
                              {(a.city?.replace('_', ' ')?.trim() === 'అల్ ఇండియా' || a.city?.replace('_', ' ')?.trim() === 'all_india') ? 'All India' : a.city?.replace('_', ' ')}
                            </span>
                          </div>
                        </td>
                        <td style={{ fontSize: '12.5px', color: '#374151' }}>
                          <span style={{ color: '#0369A1', padding: '2px 8px', background: '#E0F2FE', borderRadius: '4px', fontWeight: 500, display: 'inline-block' }}>
                            {toEnglishAuthorName(a.contributor?.name || a.author_name || a.creator?.name || '-')}
                          </span>
                        </td>
                        <td className="hide-mobile">{a.view_count}</td>
                        <td className="hide-mobile">{a.like_count}</td>
                        <td className="hide-mobile">{dateStr}</td>
                        <td className="hide-mobile">{timeStr}</td>
                        <td>
                          <span className={`badge ${a.status === 'scheduled' ? 'badge-category' : (a.status === 'published' ? (isLive(a) ? 'badge-live' : 'badge-expired') : 'badge-category')}`}>
                            {a.status === 'draft' ? 'Draft' : (a.status === 'scheduled' ? 'Scheduled' : (isLive(a) ? 'Live' : 'Expired'))}
                          </span>
                        </td>
                        {filters.status === 'scheduled' && (
                          <td>{a.status === 'scheduled' ? formatDateTime(a.published_at) : '-'}</td>
                        )}
                        <td>
                          <div className="action-btns">
                            <button className="btn btn-xs btn-ghost" onClick={() => setViewModal(a)} title="View"><MdVisibility size={14} /> View</button>
                            {(!a.is_archived) && (admin?.role !== 'reporter' || a.status === 'draft') && <button className="btn btn-xs btn-ghost" onClick={() => openEdit(a)} title="Edit"><MdEdit size={14} /> Edit</button>}
                            <button className="btn btn-xs btn-danger" onClick={() => setDeleteModal(a)} title="Delete"><MdDelete size={14} /> Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredArticles.length === 0 && (
                    <tr><td colSpan="8" className="empty-row">No articles found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {!isTopEarning && (
            <div className="mobile-cards hide-desktop">
              {filteredArticles.map(a => (
                <div key={a.id} className="mobile-card">
                  <div className="mobile-card-header">
                    <span className={`badge ${isLive(a) ? 'badge-live' : 'badge-expired'}`}>
                      {isLive(a) ? 'Live' : 'Expired'}
                    </span>
                    <span className="badge badge-category">{a.category?.replace('_', ' ')}</span>
                  </div>
                  <h4>{a.title}</h4>
                  <div className="mobile-card-stats">
                    <span>{a.view_count} views</span>
                    <span>{a.like_count} likes</span>
                    <span>{(a.city?.replace('_', ' ')?.trim() === 'అల్ ఇండియా' || a.city?.replace('_', ' ')?.trim() === 'all_india') ? 'All India' : a.city?.replace('_', ' ')}</span>
                    <span>{formatDateTime(a.published_at)}</span>
                  </div>
                  <div className="action-btns">
                    <button className="btn btn-sm btn-ghost" onClick={() => setViewModal(a)}><MdVisibility /> View</button>
                    {(!a.is_archived) && (admin?.role !== 'reporter' || a.status === 'draft') && <button className="btn btn-sm btn-ghost" onClick={() => openEdit(a)}><MdEdit /> Edit</button>}
                    <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(a)}><MdDelete /> Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isTopEarning && pagination.pages > 1 && (
            <Pagination
              pagination={pagination}
              onPageChange={fetchArticles}
            />
          )}
        </>
      )}

      {/* View Article Modal */}
      {viewModal && (
        <div className="modal-overlay" onClick={() => setViewModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '12px', paddingBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                <h2 style={{ margin: 0, fontFamily: 'Ramabhadra, serif', fontSize: 'clamp(18px, 2.5vw, 24px)', fontWeight: '400', color: '#111827', flex: 1 }}>{viewModal.title}</h2>
                <button className="modal-close" onClick={() => setViewModal(null)}><MdClose /></button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '20px' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span className="badge badge-category">{viewModal.category?.replace('_', ' ')}</span>
                  <span className={`badge ${isLive(viewModal) ? 'badge-live' : 'badge-expired'}`}>{isLive(viewModal) ? 'Live' : 'Expired'}</span>
                  <span className="badge">{(viewModal.city?.replace('_', ' ')?.trim() === 'అల్ ఇండియా' || viewModal.city?.replace('_', ' ')?.trim() === 'all_india') ? 'All India' : viewModal.city?.replace('_', ' ')}</span>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div
                    onClick={() => { setViewModal(null); navigate(`/articles/engagement/${viewModal.id}?tab=views`); }}
                    title="Click to view Engagement Details for Views"
                    style={{ padding: '8px 14px', background: '#F0F4FF', borderRadius: '8px', fontSize: '13px', color: '#2563EB', fontWeight: '600', cursor: 'pointer' }}
                  >
                    👁️ {viewModal.view_count}
                  </div>
                  <div
                    onClick={() => { setViewModal(null); navigate(`/articles/engagement/${viewModal.id}?tab=likes`); }}
                    title="Click to view Engagement Details for Likes"
                    style={{ padding: '8px 14px', background: '#FEF3C7', borderRadius: '8px', fontSize: '13px', color: '#B45309', fontWeight: '600', cursor: 'pointer' }}
                  >
                    ❤️ {viewModal.like_count}
                  </div>
                  <div
                    onClick={() => { setViewModal(null); navigate(`/articles/engagement/${viewModal.id}?tab=dislikes`); }}
                    title="Click to view Engagement Details for Dislikes"
                    style={{ padding: '8px 14px', background: '#FEE2E2', borderRadius: '8px', fontSize: '13px', color: '#DC2626', fontWeight: '600', cursor: 'pointer' }}
                  >
                    👎 {viewModal.dislike_count}
                  </div>
                  <div
                    onClick={() => { setViewModal(null); navigate(`/articles/engagement/${viewModal.id}?tab=comments`); }}
                    title="Click to view Engagement Details for Comments"
                    style={{ padding: '8px 14px', background: '#E0E7FF', borderRadius: '8px', fontSize: '13px', color: '#4F46E5', fontWeight: '600', cursor: 'pointer' }}
                  >
                    💬 {viewModal.comment_count}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '20px', fontSize: '13px', color: '#6B7280' }}>
                <span>By <strong>{toEnglishAuthorName(viewModal.contributor?.name || viewModal.author_name || viewModal.creator?.name || 'Unknown')}</strong></span>
                <span>Published: <strong>{new Date(viewModal.published_at).toLocaleDateString('en-GB')}</strong></span>
              </div>
            </div>
            <div className="modal-body modal-grid-2col">
              <div className="detail-row" style={{ gridColumn: '1 / -1' }}><span className="detail-label">Summary</span><span>{viewModal.summary}</span></div>
              {viewModal.source && (
                <div className="detail-row"><span className="detail-label">Source</span><span>{viewModal.source}</span></div>
              )}
              {viewModal.source_url && (
                <div className="detail-row"><span className="detail-label">Source URL</span><a href={viewModal.source_url} target="_blank" rel="noreferrer" className="detail-link">{viewModal.source_url}</a></div>
              )}
              {viewModal.media_type === 'video' && viewModal.video_url ? (
                <div className="detail-row" style={{ gridColumn: '2 / 3', gridRow: '2 / 5' }}>
                  <span className="detail-label">Video</span>
                  <video
                    src={viewModal.video_url}
                    poster={viewModal.thumbnail_url}
                    controls
                    className="detail-img"
                    style={{ objectFit: 'contain', background: '#000', maxHeight: '400px', width: '100%', marginTop: '8px', borderRadius: '12px' }}
                  />
                </div>
              ) : (
                viewModal.image_url && (
                  <div className="detail-row" style={{ gridColumn: '2 / 3', gridRow: '2 / 5' }}>
                    <span className="detail-label">Image</span>
                    <img src={viewModal.image_url} alt="" className="detail-img" style={{ marginTop: '8px', borderRadius: '12px', maxHeight: '400px', objectFit: 'cover' }} />
                  </div>
                )
              )}
              {viewModal.images && viewModal.images.length > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Gallery</span>
                  <div className="flex-row gap-2 flex-wrap">
                    {viewModal.images.map((img, i) => (
                      <img key={i} src={img.image_url} alt="" className="detail-img" style={{ width: 60, height: 60, objectFit: 'cover', margin: 0, borderRadius: '8px' }} />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ padding: '16px 24px', background: '#F8FAFC', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                className="btn btn-ghost"
                onClick={() => {
                  const targetId = viewModal.id;
                  setViewModal(null);
                  navigate(`/articles/engagement/${targetId}`);
                }}
                style={{ color: '#DC2626', borderColor: '#DC2626', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                View Full Engagement Analytics ➔
              </button>
              <button className="btn btn-primary" onClick={() => setViewModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Article Modal */}
      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '12px', paddingBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#111827' }}>Edit Article</h2>
                <button className="modal-close" onClick={() => setEditModal(null)}><MdClose /></button>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ fontSize: '14px', padding: '8px 16px' }}
                  onClick={() => setShowEditWebPreview(true)}
                >
                  👁️ Preview
                </button>
              </div>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <form className="modal-form" onSubmit={handleEditSubmit}>
                {/* TITLE & SUMMARY */}
                <div style={{ marginBottom: '20px' }}>
                  <div className="form-group">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <label style={{ fontSize: '13px', fontWeight: '500', color: '#111827' }}>Title</label>
                      <span style={{ fontSize: '12px', color: '#6B7280' }}>{(editForm.title || '').length}/75</span>
                    </div>
                    <input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value.slice(0, 75) }))} maxLength={75} required style={{ padding: '10px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', width: '100%', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div className="form-group">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <label style={{ fontSize: '13px', fontWeight: '500', color: '#111827' }}>Summary</label>
                      <span style={{ fontSize: '12px', color: '#6B7280' }}>{(editForm.summary || '').length}/400</span>
                    </div>
                    <textarea value={editForm.summary} onChange={e => setEditForm(f => ({ ...f, summary: e.target.value.slice(0, 400) }))} rows={5} maxLength={400} required style={{ padding: '10px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', width: '100%', boxSizing: 'border-box' }} />
                  </div>
                </div>

                {/* MEDIA TYPE */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#111827', marginBottom: '8px', display: 'block' }}>Media Type</label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <label style={{ flex: 1, padding: '10px', border: editForm.media_type === 'image' ? '1.5px solid #2563EB' : '1px solid #E5E7EB', borderRadius: '8px', textAlign: 'center', cursor: 'pointer', backgroundColor: editForm.media_type === 'image' ? '#F0F4FF' : '#FFFFFF', fontSize: '14px', fontWeight: '500', color: editForm.media_type === 'image' ? '#2563EB' : '#6B7280' }}>
                      <input type="radio" value="image" checked={editForm.media_type === 'image'} onChange={() => setEditForm(f => ({ ...f, media_type: 'image' }))} style={{ marginRight: '6px', accentColor: '#2563EB' }} />
                      Image
                    </label>
                    <label style={{ flex: 1, padding: '10px', border: editForm.media_type === 'video' ? '1.5px solid #2563EB' : '1px solid #E5E7EB', borderRadius: '8px', textAlign: 'center', cursor: 'pointer', backgroundColor: editForm.media_type === 'video' ? '#F0F4FF' : '#FFFFFF', fontSize: '14px', fontWeight: '500', color: editForm.media_type === 'video' ? '#2563EB' : '#6B7280' }}>
                      <input type="radio" value="video" checked={editForm.media_type === 'video'} onChange={() => setEditForm(f => ({ ...f, media_type: 'video' }))} style={{ marginRight: '6px', accentColor: '#2563EB' }} />
                      Video
                    </label>
                  </div>
                </div>

                {/* CATEGORY, STATE, CITY - 3 COLUMNS */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: '500', color: '#111827', marginBottom: '6px', display: 'block' }}>Category</label>
                    <SearchableSelect label="" value={editForm.category} options={categories} onChange={(val) => { setEditForm(f => ({ ...f, category: val })); let targetState = (val === 'Telangana' || val === 'తెలంగాణ' || val === 'Andhra Pradesh' || val === 'ఆంధ్రప్రదేశ్') ? (val === 'తెలంగాణ' ? 'Telangana' : val === 'ఆంధ్రప్రదేశ్' ? 'Andhra Pradesh' : val) : 'All India'; setEditState(targetState); const filtered = cities.filter(c => { if (!c.State && !c.state) return false; const cState = (c.State ? c.State.name : c.state).trim(); return cState === targetState || (targetState === 'Andhra Pradesh' && (cState === 'ఆంధ్రప్రదేశ్' || cState === 'ఆంధ్ర ప్రదేశ్')) || (targetState === 'Telangana' && cState === 'తెలంగాణ') || (targetState === 'All India' && (cState === 'అల్ ఇండియా' || cState === 'అల్ ఇండియా ')); }); setEditCities(filtered); if (filtered.length > 0) setEditForm(f => ({ ...f, city: filtered[0].name })); }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: '500', color: '#111827', marginBottom: '6px', display: 'block' }}>State</label>
                    <select value={editState} onChange={(e) => { const val = e.target.value; setEditState(val); const filtered = cities.filter(c => { if (!c.State && !c.state) return false; const cState = (c.State ? c.State.name : c.state).trim(); return cState === val || (val === 'Andhra Pradesh' && (cState === 'ఆంధ్రప్రదేశ్' || cState === 'ఆంధ్ర ప్రదేశ్')) || (val === 'Telangana' && cState === 'తెలంగాణ') || (val === 'All India' && (cState === 'అల్ ఇండియా' || cState === 'అల్ ఇండియా ')); }); setEditCities(filtered); if (filtered.length > 0) setEditForm(f => ({ ...f, city: filtered[0].name })); }} style={{ padding: '8px 10px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '13px', width: '100%', boxSizing: 'border-box' }}>
                      <option value="Andhra Pradesh">Andhra Pradesh</option>
                      <option value="Telangana">Telangana</option>
                      <option value="All India">All India</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: '500', color: '#111827', marginBottom: '6px', display: 'block' }}>City</label>
                    <SearchableSelect label="" value={editForm.city} options={editCities} onChange={(val) => setEditForm(f => ({ ...f, city: val }))} />
                  </div>
                </div>

                {/* STATUS & FEATURED */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: '500', color: '#111827', marginBottom: '6px', display: 'block' }}>Status</label>
                    <select value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))} disabled={admin?.role === 'reporter'} style={{ padding: '8px 10px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '13px', width: '100%', boxSizing: 'border-box' }}>
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', border: '1px solid #E5E7EB', borderRadius: '8px', backgroundColor: '#F9FAFB' }}>
                    <input type="checkbox" checked={editForm.is_featured} onChange={(e) => setEditForm(f => ({ ...f, is_featured: e.target.checked }))} style={{ accentColor: '#DC2626', cursor: 'pointer' }} />
                    <label style={{ fontSize: '13px', fontWeight: '500', color: '#111827', cursor: 'pointer', margin: 0 }}>Featured</label>
                  </div>
                </div>


                {/* MEDIA SECTION - FULL WIDTH */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#111827', marginBottom: '12px', display: 'block' }}>Media</label>
                  {editForm.media_type === 'image' && (
                    <>
                      <div className="upload-area" tabIndex={0} onClick={() => handleEditFileSelect('image')} onPaste={handleEditPasteImage} style={{ minHeight: '220px', borderRadius: '10px', border: '2px dashed #E5E7EB', cursor: 'pointer' }}>
                        {editForm.image_url ? (
                          <img src={editForm.image_url} alt="preview" className="upload-preview" />
                        ) : (
                          <p style={{ color: '#9CA3AF', fontSize: '14px' }}>{editUploading ? 'Processing...' : 'Click to upload image'}</p>
                        )}
                      </div>

                      {/* Gallery Images Section */}
                      <div className="form-group" style={{ marginTop: '20px' }} tabIndex={0} onPaste={handleEditGalleryPasteImage}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <label style={{ margin: 0, fontWeight: 700, fontSize: '13px', color: '#111827' }}>Gallery Images</label>
                          <span style={{ fontSize: '12px', color: '#6B7280' }}>Click, drag & drop, or paste (Ctrl+V)</span>
                        </div>
                        <div 
                          className="upload-area"
                          tabIndex={0}
                          onPaste={handleEditGalleryPasteImage}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.multiple = true;
                            input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
                            input.onchange = (e) => handleEditGalleryFilesUpload(Array.from(e.target.files));
                            input.click();
                          }}
                          style={{ cursor: 'pointer', padding: '24px 16px', border: '2px dashed #CBD5E1', borderRadius: '10px', textAlign: 'center', backgroundColor: '#F8FAFC' }}
                        >
                          <p style={{ margin: 0, fontSize: '14px', color: '#475569', fontWeight: 500 }}>
                            {editGalleryUploading ? 'Uploading gallery images...' : 'Click to select multiple images, drag & drop, or paste (Ctrl+V)'}
                          </p>
                        </div>

                        {/* Gallery Thumbnails Grid & Plus Card */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '12px', marginTop: '16px' }}>
                          {(editForm.images || []).map((imgUrl, index) => (
                            <div key={index} style={{ position: 'relative', borderRadius: '8px', border: '1px solid #E2E8F0', overflow: 'hidden', backgroundColor: '#FFF', height: '108px', display: 'flex', flexDirection: 'column' }}>
                              <img src={typeof imgUrl === 'string' ? imgUrl : imgUrl.image_url || imgUrl.url} alt={`gallery-${index}`} style={{ width: '100%', height: '80px', objectFit: 'cover', display: 'block' }} />
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 6px', background: '#F1F5F9', borderTop: '1px solid #E2E8F0', height: '28px', boxSizing: 'border-box' }}>
                                <div style={{ display: 'flex', gap: '2px' }}>
                                  <button 
                                    type="button" 
                                    disabled={index === 0} 
                                    onClick={(e) => { e.stopPropagation(); moveEditGalleryImage(index, -1); }} 
                                    style={{ border: 'none', background: 'transparent', cursor: index === 0 ? 'default' : 'pointer', opacity: index === 0 ? 0.3 : 1, fontSize: '12px', padding: '2px 4px' }}
                                    title="Move Left"
                                  >
                                    ◀
                                  </button>
                                  <button 
                                    type="button" 
                                    disabled={index === (editForm.images || []).length - 1} 
                                    onClick={(e) => { e.stopPropagation(); moveEditGalleryImage(index, 1); }} 
                                    style={{ border: 'none', background: 'transparent', cursor: index === (editForm.images || []).length - 1 ? 'default' : 'pointer', opacity: index === (editForm.images || []).length - 1 ? 0.3 : 1, fontSize: '12px', padding: '2px 4px' }}
                                    title="Move Right"
                                  >
                                    ▶
                                  </button>
                                </div>
                                <button 
                                  type="button" 
                                  onClick={(e) => { e.stopPropagation(); removeEditGalleryImage(index); }} 
                                  style={{ border: 'none', background: 'transparent', color: '#EF233C', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', padding: '2px 4px' }}
                                  title="Remove Image"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ))}

                          {/* Add Images (+) Card */}
                          <div 
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.multiple = true;
                              input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
                              input.onchange = (e) => handleEditGalleryFilesUpload(Array.from(e.target.files));
                              input.click();
                            }}
                            style={{
                              height: '108px',
                              border: '2px dashed #CBD5E1',
                              borderRadius: '8px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              backgroundColor: '#F8FAFC',
                              transition: 'all 0.2s ease',
                              color: '#475569',
                              boxSizing: 'border-box'
                            }}
                            title="Add Images"
                          >
                            <span style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1 }}>+</span>
                            <span style={{ fontSize: '12px', fontWeight: 600, marginTop: '4px' }}>Add Images</span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {editForm.media_type === 'video' && (
                    <>
                      <input type="text" value={editForm.video_url} onChange={e => setEditForm(f => ({ ...f, video_url: e.target.value }))} placeholder="MP4 URL..." style={{ padding: '10px 12px', border: '1.5px solid #E5E7EB', borderRadius: '10px', width: '100%', marginBottom: '12px', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                      <div className="upload-area" onClick={() => handleEditFileSelect('thumbnail')} style={{ minHeight: '180px', borderRadius: '10px', border: '2px dashed #E5E7EB', cursor: 'pointer' }}>
                        {editForm.thumbnail_url ? (
                          <img src={editForm.thumbnail_url} alt="thumbnail" className="upload-preview" />
                        ) : (
                          <p style={{ color: '#9CA3AF', fontSize: '14px' }}>{editUploading ? 'Processing...' : 'Click to upload thumbnail'}</p>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* ADDITIONAL FIELDS */}
                <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 32px' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '13px', fontWeight: '500', color: '#111827', margin: 0, marginBottom: '6px', display: 'block' }}>Source</label>
                    <input type="text" value={editForm.source} onChange={e => setEditForm(f => ({ ...f, source: e.target.value }))} placeholder="e.g. Times of India..." style={{ padding: '10px 12px', border: '1.5px solid #E5E7EB', borderRadius: '10px', width: '100%', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '13px', fontWeight: '500', color: '#111827', margin: 0, marginBottom: '6px', display: 'block' }}>Source URL</label>
                    <input type="url" value={editForm.source_url} onChange={e => setEditForm(f => ({ ...f, source_url: e.target.value }))} placeholder="https://..." style={{ padding: '10px 12px', border: '1.5px solid #E5E7EB', borderRadius: '10px', width: '100%', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Article</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this article?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>"{deleteModal.title}"</p>
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}


      {showEditWebPreview && (
        <WebPreviewModal
          article={editForm}
          onClose={() => setShowEditWebPreview(false)}
        />
      )}

      {editCropper.active && (
        <ImageCropper
          image={editCropper.image}
          onCropComplete={handleEditCropComplete}
          onCancel={() => setEditCropper({ active: false, image: null, type: null })}
        />
      )}
    </div>
  );
}
